import React, { lazy, Suspense, useEffect, useMemo, useState } from "react";
import "../../../../../shared/cookiebotOverrides.css";

import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import { ExperimentProvider } from "../Experiment";
import Spinner from "../Spinner";
import FirstLoadScreen from "../FirstLoadScreen";
import CoveredContent from "../../../../../shared/LogoLoader/CoveredContent";
import { routes } from "../../routes";
import paths from "../../routes/paths";
import "../../styles/base.scss";
import useCampaignSessionTracking from "../../hooks/useCampaignSessionTracking";
import useCookieConsentPersistence from "../../hooks/useCookieConsentPersistence";
import useMixpanelLinkTracking from "../../hooks/useMixpanelLinkTracking";
import usePortalPageLifecycleEvents, {
  recordPortalPageLoaded,
} from "../../hooks/usePortalPageLifecycleEvents";
import useStateView from "../../hooks/useStateView";
import {
  accountView,
  redirectView,
  selectedReferenceNumberView,
  sessionView,
  shortLinkRedirectView,
} from "../../state/views";
import { hasSettledBalance } from "../../utils/currency";
import { hasAuthHandoffPayload } from "../../utils/authHandoff";
import { initializeGtmDataLayer } from "../../utils/gtmDataLayer";
import {
  isChunkLoadFailure,
  recoverFromChunkLoadFailure,
} from "../../utils/chunkLoadRecovery";
import mixpanel from "../../utils/mixpanel";
import ExperimentTracker from "../Experiment/ExperimentTracker";

const AuthBarrierRoute = lazy(() =>
  import(/* webpackChunkName: "auth-barrier-route" */ "../AuthBarrierRoute")
);

const {
  initializeCookiebotBannerEnhancer,
  syncCookiebotCulture,
} = require("../../../../../shared/cookiebotBannerUi");

const LOGIN_READY_EVENT = "soc:login-form-ready";

const settledBalanceBlockedPaths = new Set([
  paths.paymentPlan,
  paths.paymentPlanSetup,
  paths.paymentConfirm,
]);

class ChunkLoadBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      error: null,
      isChunkLoadFailure: false,
    };
  }

  static getDerivedStateFromError(error) {
    return {
      error,
      isChunkLoadFailure: isChunkLoadFailure(error),
    };
  }

  componentDidCatch(error) {
    if (isChunkLoadFailure(error)) {
      recoverFromChunkLoadFailure(error);
    }
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return <Spinner variant="large" />;
  }
}

const scheduleAfterPaint = (callback) => {
  if (typeof window === "undefined") return () => {};

  let idleId;
  const frameId = window.requestAnimationFrame(() => {
    if (typeof window.requestIdleCallback === "function") {
      idleId = window.requestIdleCallback(callback, { timeout: 1500 });
      return;
    }

    idleId = window.setTimeout(callback, 0);
  });

  return () => {
    window.cancelAnimationFrame(frameId);
    if (typeof window.cancelIdleCallback === "function") {
      window.cancelIdleCallback(idleId);
    } else if (idleId) {
      window.clearTimeout(idleId);
    }
  };
};

const loadCookiebotCmp = () => {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (window.__ENV?.ENABLE_COOKIEBOT_CMP !== "true") return;
  if (window.Cookiebot || document.getElementById("Cookiebot")) return;

  const cookiebotId = window.__ENV?.COOKIEBOT_ID;
  if (!cookiebotId) return;

  const script = document.createElement("script");
  script.id = "Cookiebot";
  script.setAttribute("data-cfasync", "false");
  script.src = "https://consent.cookiebot.com/uc.js";
  script.setAttribute("data-cbid", cookiebotId);
  // The portal gates app-owned third-party tools explicitly. Cookiebot auto mode
  // can intercept Quiq's nested runtime scripts and prevent the launcher mount.
  script.setAttribute("data-blockingmode", "manual");

  const culture = syncCookiebotCulture(window, document);
  if (culture) {
    script.setAttribute("data-culture", culture);
  }

  script.type = "text/javascript";
  script.async = true;
  document.head.appendChild(script);

  const waitForCookiebot = () => {
    if (window.Cookiebot) {
      window.dispatchEvent(new Event("soc:cookiebot-cmp-loaded"));
      return;
    }
    window.setTimeout(waitForCookiebot, 50);
  };
  waitForCookiebot();
};

const AuthRedirectGate = ({ children }) => {
  const [session] = useStateView(sessionView);
  const [, setRedirectUrl] = useStateView(redirectView);
  const [, setSelectedReferenceNumber] = useStateView(selectedReferenceNumberView);
  const shouldBypassAuth = process.env.PREVIEW_AUTH_BYPASS === "true";
  const userIsUnauthenticated =
    !session.authenticated || (session.expires && session.expires < Date.now());
  const shouldRedirectToLogin =
    !shouldBypassAuth && userIsUnauthenticated && !hasAuthHandoffPayload();

  useEffect(() => {
    if (!shouldRedirectToLogin) return;

    const { href, origin } = document.location;
    const newRedirectUrl = href.substring(origin.length);
    setRedirectUrl(newRedirectUrl === "/" ? "" : newRedirectUrl);
    setSelectedReferenceNumber(null);
    mixpanel.reset();
  }, [setRedirectUrl, setSelectedReferenceNumber, shouldRedirectToLogin]);

  if (shouldRedirectToLogin) {
    return <Navigate to={paths.login} replace />;
  }

  return children;
};

const SettledBalanceRedirectGate = ({ children, path }) => {
  const [account] = useStateView(accountView);
  const [shortLinkRedirect] = useStateView(shortLinkRedirectView);
  const confirmationReferenceNumber = String(
    shortLinkRedirect?.referenceNumber || ""
  ).trim();
  const loadedAccountId = String(account?.accountId || "").trim();
  const confirmsAnotherAccount =
    path === paths.paymentConfirm &&
    confirmationReferenceNumber &&
    confirmationReferenceNumber !== loadedAccountId;

  if (
    hasSettledBalance(account?.remainingBalance) &&
    settledBalanceBlockedPaths.has(path) &&
    !confirmsAnotherAccount
  ) {
    return <Navigate to={paths.home} replace />;
  }

  return children;
};

const PortalRouteLoaded = ({ children }) => {
  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const frameId = window.requestAnimationFrame(recordPortalPageLoaded);
    return () => window.cancelAnimationFrame(frameId);
  }, []);

  return children;
};

const App = () => {
  const [enableDeferredStartup, setEnableDeferredStartup] = useState(false);

  useCampaignSessionTracking(enableDeferredStartup);
  useCookieConsentPersistence();
  useMixpanelLinkTracking(enableDeferredStartup);
  usePortalPageLifecycleEvents();

  useEffect(() => {
    let started = false;
    let cleanupScheduledStartup = () => {};

    const startDeferredStartup = () => {
      if (started) return;
      started = true;

      cleanupScheduledStartup = scheduleAfterPaint(() => {
        initializeCookiebotBannerEnhancer();
        loadCookiebotCmp();
        initializeGtmDataLayer();
        setEnableDeferredStartup(true);
      });
    };

    const shouldWaitForLoginForm =
      typeof window !== "undefined" && window.location.pathname === paths.login;

    if (!shouldWaitForLoginForm) {
      startDeferredStartup();
      return () => cleanupScheduledStartup();
    }

    window.addEventListener(LOGIN_READY_EVENT, startDeferredStartup, { once: true });
    const fallbackId = window.setTimeout(startDeferredStartup, 2500);

    return () => {
      window.removeEventListener(LOGIN_READY_EVENT, startDeferredStartup);
      window.clearTimeout(fallbackId);
      cleanupScheduledStartup();
    };
  }, []);

  const mappedRoutes = useMemo(
    () =>
      routes.map(({ path, hideNav, component: Component, requireAuth }) => {
        const page = (
          <ChunkLoadBoundary>
            <Suspense fallback={<Spinner variant="large" />}>
              <PortalRouteLoaded>
                <Component />
              </PortalRouteLoaded>
            </Suspense>
          </ChunkLoadBoundary>
        );
        const authBarrierElement = (
          <ChunkLoadBoundary>
            <Suspense fallback={<Spinner variant="large" />}>
              <AuthBarrierRoute path={path} active={requireAuth} hideNav={hideNav}>
                {page}
              </AuthBarrierRoute>
            </Suspense>
          </ChunkLoadBoundary>
        );
        const settledBalanceElement = (
          <SettledBalanceRedirectGate path={path}>
            {authBarrierElement}
          </SettledBalanceRedirectGate>
        );
        const element =
          path === paths.login
            ? page
            : requireAuth
              ? (
                  <AuthRedirectGate>
                    {settledBalanceElement}
                  </AuthRedirectGate>
                )
              : settledBalanceElement;

        return (
          <Route
            key={`route-${path}`}
            path={path}
            element={element}
          />
        );
      }),
    [routes]
  );

  return (
    <ExperimentProvider>
      <ExperimentTracker enabled={enableDeferredStartup} />
      <FirstLoadScreen />
      <CoveredContent>
        <BrowserRouter>
          <Routes> {mappedRoutes} </Routes>
        </BrowserRouter>
      </CoveredContent>
    </ExperimentProvider>
  );
};

export default App;
